import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import { useRef, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { IsMobile } from "utils/is-mobile";
import MaskAndValid from "utils/maskAndValid";
import InputText from "components/_refactor/text/input";
import DialogGalery from "./dialog-galery";
import {
  AddPhotoAlternateOutlined,
  CollectionsOutlined,
} from "@mui/icons-material";

const MAX_IMAGES = 4;
const MAX_IMAGE_SIZE = 1024 * 1024; // 1MB

export default function ChooseInvoiceDialog({
  onClose,
  invoice,
  selectedProductsList,
  setSelectedProductsList,
  motivoRequerEvidencia,
}) {
  const [products, setProducts] = useState(
    invoice.items.map((product) => ({
      ...product,
      images: [],
    }))
  );

  const [quantityError, setQuantityError] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [hasSelected, setHasSelected] = useState(false);

  const [showingPhoto, setShowingPhoto] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [checkbox, setCheckbox] = useState(false);

  const isMobile = IsMobile();
  const snackbar = useSnackbar();
  const fileInputRefs = useRef({});

  const handleChangeQuantity = (product, quantity) => {
    setQuantityError(false);
    setProducts(
      products.map((p) =>
        p.idProduto === product.idProduto ? { ...p, quantity: +quantity } : p
      )
    );
    if (quantity > product.QtdFaturada - product.QuantidadeDevolvida) {
      setQuantityError(true);
    }
  };

  const handleChangeSelected = (codigo) => {
    const newProducts = products.map((p) =>
      p.idProduto === codigo ? { ...p, Selected: !p.Selected } : p
    );
    setProducts(newProducts);
    const hasSelected = newProducts.some((p) => p.Selected);
    setHasSelected(hasSelected);
  };

  const handleImageUpload = (event, productId) => {
    const files = Array.from(event.target.files);
    const currentProduct = products.find((p) => p.idProduto === productId);
    const currentImages = currentProduct?.images || [];

    if (currentImages.length + files.length > MAX_IMAGES) {
      snackbar.showMessage(
        `Máximo de ${MAX_IMAGES} imagens permitido por produto`,
        {
          severity: "error",
        }
      );
      return;
    }

    const validFiles = [];
    files.forEach((file) => {
      if (file.size > MAX_IMAGE_SIZE) {
        snackbar.showMessage(`Tamanho máximo de imagem permitido é 1MB`, {
          severity: "error",
        });
        return;
      }
      validFiles.push(file);
    });

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.idProduto === productId
              ? {
                  ...product,
                  images: [
                    ...(product.images || []),
                    {
                      base64: reader.result,
                      name: file.name,
                      extension: file.name.split(".").pop().toLowerCase(),
                    },
                  ],
                }
              : product
          )
        );

        if (showingPhoto && showingPhoto.productId === productId) {
          const updatedProduct = products.find(
            (p) => p.idProduto === productId
          );
          setShowingPhoto({
            ...showingPhoto,
            images: [
              ...(updatedProduct?.images || []),
              {
                base64: reader.result,
                name: file.name,
                extension: file.name.split(".").pop().toLowerCase(),
              },
            ],
          });
        }
      };
      reader.readAsDataURL(file);
    });

    event.target.value = "";
  };

  const handleRemoveImage = (productId, imageIndex) => {
    setProducts((prevProducts) =>
      prevProducts.map((product) =>
        product.idProduto === productId
          ? {
              ...product,
              images: product.images.filter((_, index) => index !== imageIndex),
            }
          : product
      )
    );

    if (showingPhoto && showingPhoto.productId === productId) {
      const updatedProduct = products.find((p) => p.idProduto === productId);
      const updatedImages = updatedProduct.images.filter(
        (_, index) => index !== imageIndex
      );

      if (updatedImages.length === 0) {
        setShowingPhoto(null);
      } else {
        const newIndex =
          imageIndex >= updatedImages.length
            ? updatedImages.length - 1
            : currentImageIndex;
        setCurrentImageIndex(newIndex);
        setShowingPhoto({
          ...showingPhoto,
          images: updatedImages,
        });
      }
    }
  };

  const handleOpenImageModal = (product) => {
    setShowingPhoto({
      productId: product.idProduto,
      productName: product.name,
      images: product.images || [],
    });
    setCurrentImageIndex(0);
  };

  const handleCloseModal = () => {
    setShowingPhoto(null);
    setCurrentImageIndex(0);
  };

  const handleAddImagesFromModal = (productId) => {
    if (fileInputRefs.current[productId]) {
      fileInputRefs.current[productId].click();
    }
  };

  const validateSelectedProducts = () => {
    const selectedProducts = products.filter((product) => product.Selected);
    const valid = selectedProducts.every((product) => {
      const qtyOk = product.quantity > 0;
      const evidenceOk = !motivoRequerEvidencia || (product.images && product.images.length > 0);
      return qtyOk && evidenceOk;
    });
    setIsValid(valid);
    return valid;
  };

  const handleClose = () => {
    const toAdd = products
      .filter((product) => product.Selected)
      .map((p) => ({
        ...p,
        NumeroNf: invoice?.NumeroNf ?? p.NumeroNf,
        SerieNf: invoice?.SerieNf ?? p.SerieNf,
        ChaveNf: invoice?.ChaveNf ?? p.ChaveNf,
        idTableProduct: `${p.idProduto}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        Selected: undefined,
      }));
    setSelectedProductsList([...selectedProductsList, ...toAdd]);
    onClose();
  };

  const handleSubmit = () => {
    const valid = validateSelectedProducts();
    if (valid) {
      handleClose();
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth={"xl"}>
      <PerfectScrollbar>
        <DialogTitle>
          <Typography variant="h3" component="h3">
            Escolha os Produtos para Vincular a Solicitação
          </Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: "0" }}>
          <Grid
            container
            spacing={2}
            direction={isMobile ? "column" : "row"}
            style={{ margin: 0 }}
          >
            <Grid item xs={12} md={3}>
              <Typography variant="h4" component="h3">
                Nota Fiscal:
                <span style={{ fontWeight: "400" }}> {invoice?.NumeroNf}</span>
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h4" component="h3">
                Serie:
                <span style={{ fontWeight: "400" }}> {invoice?.SerieNf}</span>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h3">
                Data de Emissão:
                <span style={{ fontWeight: "400" }}>
                  {" "}
                  {invoice?.DataEmissaoNf}
                </span>
              </Typography>
            </Grid>
            {motivoRequerEvidencia && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  * O motivo da solicitação exige evidência (imagem) em cada produto selecionado.
                </Typography>
              </Grid>
            )}
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Checkbox
                          checked={checkbox}
                          onChange={() => {
                            setProducts(
                              products.map((product) => ({
                                ...product,
                                Selected: !checkbox,
                              }))
                            );
                            setCheckbox(!checkbox);
                          }}
                          name="ativo"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="center">Cód Pedido</TableCell>
                      <TableCell align="center">Cód Produto</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell align="center">Qtd NF</TableCell>
                      <TableCell align="center">Qtd Devolvida</TableCell>
                      <TableCell align="center">Valor unitário</TableCell>
                      <TableCell align="center">Valor Total</TableCell>
                      <TableCell align="center">Devolução</TableCell>
                      <TableCell align="center">Imagens</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => {
                      const productImages = product.images || [];
                      return (
                        <TableRow key={product.idProduto}>
                          <TableCell>
                            <Checkbox
                              checked={product.Selected || false}
                              onChange={() => {
                                handleChangeSelected(product.idProduto);
                              }}
                              name="ativo"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ alignContent: "center" }}
                          >
                            {product.IdPedido}
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ alignContent: "center" }}
                          >
                            {product.idProduto}
                          </TableCell>
                          <TableCell style={{ alignContent: "center" }}>
                            {product.name}
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ alignContent: "center" }}
                          >
                            {product.QtdFaturada}
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ alignContent: "center" }}
                          >
                            {product.QuantidadeDevolvida}
                          </TableCell>
                          <TableCell style={{ alignContent: "center" }}>
                            R$ {MaskAndValid.maskPrice(product.ValorUnitario)}
                          </TableCell>
                          <TableCell style={{ alignContent: "center" }}>
                            R$ {MaskAndValid.maskPrice(product.ValorTotal)}
                          </TableCell>
                          <TableCell style={{ alignContent: "center" }}>
                            <InputText
                              type="text"
                              value={product.quantidade}
                              onChange={(e) =>
                                handleChangeQuantity(
                                  product,
                                  e.target.value.replace(/\D/g, "")
                                )
                              }
                            />
                          </TableCell>
                          <TableCell style={{ alignContent: "center" }}>
                            <div style={{ textAlign: "center" }}>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(event) =>
                                  handleImageUpload(event, product.idProduto)
                                }
                                ref={(ref) =>
                                  (fileInputRefs.current[product.idProduto] =
                                    ref)
                                }
                                style={{ display: "none" }}
                              />

                              {productImages.length === 0 ? (
                                <IconButton
                                  onClick={() =>
                                    fileInputRefs.current[
                                      product.idProduto
                                    ]?.click()
                                  }
                                >
                                  <AddPhotoAlternateOutlined fontSize="small" />
                                </IconButton>
                              ) : (
                                <IconButton
                                  onClick={() => handleOpenImageModal(product)}
                                >
                                  <CollectionsOutlined fontSize="small" />
                                </IconButton>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          {quantityError && (
            <Grid>
              <Typography
                variant="h5"
                component="h5"
                style={{ color: "grey", marginTop: "1rem" }}
              >
                <i
                  className="fa fa-exclamation-circle"
                  aria-hidden="true"
                  style={{
                    fontSize: "1rem",
                    marginRight: "0.5rem",
                  }}
                />{" "}
                Quantidade de devolução maior que a quantidade disponível
              </Typography>
            </Grid>
          )}

          {!isValid && (
            <Grid>
              <Typography
                variant="h5"
                component="h5"
                style={{ color: "grey", marginTop: "1rem" }}
              >
                <i
                  className="fa fa-exclamation-circle"
                  aria-hidden="true"
                  style={{
                    fontSize: "1rem",
                    marginRight: "0.5rem",
                  }}
                />{" "}
                {motivoRequerEvidencia
                  ? "É necessário informar quantidade e evidência (imagem) em cada produto selecionado."
                  : "É necessário informar a quantidade em cada produto selecionado."}
              </Typography>
            </Grid>
          )}
        </DialogContent>
        <DialogActions style={{ justifyContent: "center" }}>
          <Grid item xs={3}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={quantityError || !hasSelected}
              style={{ width: "100%" }}
            >
              Adicionar
            </Button>
          </Grid>
        </DialogActions>
      </PerfectScrollbar>

      {showingPhoto && (
        <DialogGalery
          onClose={handleCloseModal}
          images={showingPhoto?.images || []}
          AddImage={() => handleAddImagesFromModal(showingPhoto.productId)}
          RemoveImage={() =>
            handleRemoveImage(showingPhoto.productId, currentImageIndex)
          }
          MAX_IMAGES={MAX_IMAGES}
        />
      )}
    </Dialog>
  );
}
